import api from '@/utils/axios';

/**
 * 채권 관련 API 서비스
 * - 국고채(KTB) 관리
 * - 통안채(MSB) 관리  
 * - 크레딧 채권 관리
 * - 발행사 관리
 * - 그룹 관리
 * - 파일 업로드
 */
const bondService = {
  // ================================
  // 채권 타입 조회 API
  // ================================
  async getKtbBondTypes() {
    try {
      const data = await api.get('/api/v1/ktb-bonds/types');
      return data;
    } catch (error) {
      console.error('국고채 타입 조회 실패:', error);
      throw error;
    }
  },

  async getMsbBondTypes() {
    try {
      const data = await api.get('/api/v1/msb-bonds/types');
      return data;
    } catch (error) {
      console.error('통안채 타입 조회 실패:', error);
      throw error;
    }
  },

  async getCreditBondTypes() {
    try {
      const data = await api.get('/api/v1/credit-bonds/types');
      return data;
    } catch (error) {
      console.error('크레딧 타입 조회 실패:', error);
      throw error;
    }
  },

  // ================================
  // 국고채(KTB) API
  // ================================
  async getKtbBonds(baseDate) {
    try {
      const data = await api.get(`/api/v1/ktb-bonds/daily?baseDate=${baseDate}`);
      return data;
    } catch (error) {
      console.error('국고채 정보 조회 실패:', error);
      throw error;
    }
  },

  async createKtbBond(bondData) {
    try {
      const response = await api.post('/api/v1/ktb-bonds', {
        baseDate: bondData.baseDate,
        code: bondData.code,
        bondName: bondData.bondName,
        yield: bondData.yield,
        isBenchmark: bondData.isBenchmark,
        maturityDate: bondData.maturityDate,
        maturityRangeType: bondData.maturityRangeType,
      });
      return response;
    } catch (error) {
      console.error('국고채 생성 실패:', error);
      throw error;
    }
  },

  async updateKtbBond(bondData) {
    try {
      const response = await api.put('/api/v1/ktb-bonds', {
        baseDate: bondData.baseDate,
        code: bondData.code,
        bondName: bondData.bondName,
        yield: bondData.yield,
        isBenchmark: bondData.isBenchmark,
        maturityRangeType: bondData.maturityRangeType,
      });
      return response;
    } catch (error) {
      console.error('국고채 정보 업데이트 실패:', error);
      throw error;
    }
  },

  // ================================
  // 통안채(MSB) API
  // ================================
  async getMsbBonds(baseDate) {
    try {
      const data = await api.get(`/api/v1/msb-bonds/daily?baseDate=${baseDate}`);
      return data;
    } catch (error) {
      console.error('통안채 정보 조회 실패:', error);
      throw error;
    }
  },

  async updateMsbBond(bondData, baseDate) {
    try {
      const response = await api.put('/api/v1/msb-bonds', {
        baseDate: baseDate,
        code: bondData.code,
        bondName: bondData.bondName,
        yield: bondData.yield,
        msbType: bondData.msbType,
      });
      return response;
    } catch (error) {
      console.error('통안채 정보 업데이트 실패:', error);
      throw error;
    }
  },

  // ================================
  // 크레딧 채권 API
  // ================================
  async getCreditIssuance(creditType, months = 3) {
    try {
      const data = await api.get('/api/v1/credit-bonds/issuance/recent', {
        creditType,
        months,
      });
      return data;
    } catch (error) {
      console.error('크레딧 발행정보 조회 실패:', error);
      throw error;
    }
  },

  async fetchIssuanceFromInfomax(code) {
    try {
      const data = await api.get(`/api/v1/credit-bonds/issuance/fetch?code=${code}`);
      return data;
    } catch (error) {
      console.error('인포맥스 발행정보 조회 실패:', error);
      throw error;
    }
  },

  async updateIssuance(issuanceData) {
    try {
      await api.put('/api/v1/credit-bonds/issuance', {
        code: issuanceData.code,
        status: issuanceData.status,
        leadManagers: issuanceData.leadManagers,
        underwriters: issuanceData.underwriters,
      });
    } catch (error) {
      console.error('발행정보 업데이트 실패:', error);
      throw error;
    }
  },

  async updateIssuanceByCode(code) {
    try {
      const response = await api.post('/api/v1/credit-bonds/issuance/update', {
        code,
      });
      return response;
    } catch (error) {
      console.error('발행정보 개별 업데이트 실패:', error);
      throw error;
    }
  },

  async batchUpdateIssuance(codes) {
    try {
      await api.post('/api/v1/credit-bonds/issuance/batch-update', {
        codes,
      });
    } catch (error) {
      console.error('발행정보 일괄 업데이트 실패:', error);
      throw error;
    }
  },

  async refreshIssuance() {
    try {
      await api.post('/api/v1/credit-bonds/issuance/refresh');
    } catch (error) {
      console.error('발행정보 새로고침 실패:', error);
      throw error;
    }
  },

  async refreshPrices(date, bondType) {
    try {
      const data = await api.put(`/api/v1/bonds/refresh-prices?date=${date}&bondType=${bondType}`);
      return data;
    } catch (error) {
      console.error('INFOMAX 민평 일괄 업데이트 실패:', error);
      throw error;
    }
  },

  // ================================
  // 발행사 관리 API
  // ================================
  async getCreditIssuers() {
    try {
      const data = await api.get('/api/v1/issuer/with-alias');
      return data;
    } catch (error) {
      console.error('크레딧 발행사 조회 실패:', error);
      throw error;
    }
  },

  async createIssuer(issuerData) {
    try {
      await api.post('/api/v1/issuer', {
        bondType: issuerData.type,
        name: issuerData.name,
        grade: issuerData.grade,
      });
    } catch (error) {
      console.error('발행사 생성 실패:', error);
      throw error;
    }
  },

  async updateIssuer(issuerId, issuerData) {
    try {
      const response = await api.patch(`/api/v1/issuer/${issuerId}`, {
        name: issuerData.name,
        grade: issuerData.grade,
      });
      return response;
    } catch (error) {
      console.error('발행사 수정 실패:', error);
      throw error;
    }
  },

  async deleteIssuer(issuerId) {
    try {
      await api.delete(`/api/v1/issuer/${issuerId}`);
    } catch (error) {
      console.error('발행사 삭제 실패:', error);
      throw error;
    }
  },

  async createAlias(issuerId, aliasName) {
    try {
      const response = await api.post(`/api/v1/issuer/${issuerId}/alias`, {
        name: aliasName,
      });
      return response;
    } catch (error) {
      console.error('별칭 생성 실패:', error);
      throw error;
    }
  },

  async deleteAlias(issuerId, aliasId) {
    try {
      await api.delete(`/api/v1/issuer/${issuerId}/alias/${aliasId}`);
    } catch (error) {
      console.error('별칭 삭제 실패:', error);
      throw error;
    }
  },

  // ================================
  // 그룹 관리 API
  // ================================
  async getBondGroups(creditType) {
    try {
      const data = await api.get('/api/v1/bond-groups', {
        creditType,
      });
      return data;
    } catch (error) {
      console.error('채권 그룹 조회 실패:', error);
      throw error;
    }
  },

  async getBondGroup(groupId) {
    try {
      const data = await api.get(`/api/v1/bond-groups/${groupId}`);
      return data;
    } catch (error) {
      console.error('채권 그룹 상세 조회 실패:', error);
      throw error;
    }
  },

  async createBondGroup(groupData) {
    try {
      const response = await api.post('/api/v1/bond-groups', {
        name: groupData.name,
        creditType: groupData.creditType,
        issuerIds: groupData.issuerIds,
      });
      return response;
    } catch (error) {
      console.error('채권 그룹 생성 실패:', error);
      throw error;
    }
  },

  async updateBondGroup(groupId, groupData) {
    try {
      const response = await api.put(`/api/v1/bond-groups/${groupId}`, {
        name: groupData.name,
        creditType: groupData.creditType,
        issuerIds: groupData.issuerIds,
      });
      return response;
    } catch (error) {
      console.error('채권 그룹 수정 실패:', error);
      throw error;
    }
  },

  async deleteBondGroup(groupId) {
    try {
      await api.delete(`/api/v1/bond-groups/${groupId}`);
    } catch (error) {
      console.error('채권 그룹 삭제 실패:', error);
      throw error;
    }
  },

  async addIssuerToGroup(groupId, issuerId) {
    try {
      await api.post(`/api/v1/bond-groups/${groupId}/issuers/${issuerId}`);
    } catch (error) {
      console.error('그룹에 발행사 추가 실패:', error);
      throw error;
    }
  },

  async removeIssuerFromGroup(groupId, issuerId) {
    try {
      await api.delete(`/api/v1/bond-groups/${groupId}/issuers/${issuerId}`);
    } catch (error) {
      console.error('그룹에서 발행사 제거 실패:', error);
      throw error;
    }
  },

  // ================================
  // 파일 업로드 및 분석 API
  // ================================
  async uploadBondFile(date, fileName, bondType, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.postFormData(
        `/api/v1/upload/bond-price?date=${date}&fileName=${fileName}&bondType=${bondType}`,
        formData
      );
      return response;
    } catch (error) {
      console.error('채권 파일 업로드 실패:', error);
      throw error;
    }
  },

  async upsertBondOfDay(date, bondType) {
    try {
      await api.post('/api/v1/bonds/upsert', {
        date,
        bondType,
      });
    } catch (error) {
      console.error('채권 데이터 분석 실패:', error);
      throw error;
    }
  },

  // ================================
  // 기타 API
  // ================================
  async getExclusionKeywords(targetType) {
    try {
      const data = await api.get('/api/v1/exclusion-keywords', {
        targetType,
      });
      return data;
    } catch (error) {
      console.error('제외 키워드 조회 실패:', error);
      throw error;
    }
  },

  async createExclusionKeyword(keyword, targetType) {
    try {
      const response = await api.post('/api/v1/exclusion-keywords', {
        keyword,
        targetType,
      });
      return response;
    } catch (error) {
      console.error('제외 키워드 생성 실패:', error);
      throw error;
    }
  },

  async deleteExclusionKeyword(id) {
    try {
      await api.delete(`/api/v1/exclusion-keywords/${id}`);
    } catch (error) {
      console.error('제외 키워드 삭제 실패:', error);
      throw error;
    }
  },

  // ================================
  // Yuanta 페이지 관련 API
  // ================================
  async getDailySyncRecord(date) {
    try {
      const data = await api.get('/api/v1/sync/daily-record', {
        date,
      });
      return data;
    } catch (error) {
      console.error('일일 동기화 기록 조회 실패:', error);
      throw error;
    }
  },

  async getCreditQuotes(date) {
    try {
      const data = await api.get('/api/v1/quote/credit/all', {
        date,
      });
      return data;
    } catch (error) {
      console.error('크레딧 호가 조회 실패:', error);
      throw error;
    }
  },
};

export default bondService;
